import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import { Card, Col, Row } from 'reactstrap';
import { Button, addToast } from '@octano/global-ui';

import GoBackToListButton from '../../../../../components/buttons/GoBackToListButton';
import { TranslationsKeys } from '../../../../../locales/translations';
import DisplayError from '../../../../../components/info/DisplayError';
import Loading from '../../../../../components/info/Loading';
import {
  getHomologationRequestDetail,
  sendToSignature,
  downloadActaPdf,
} from '../../api';
import {
  HomologationRequestDetail,
  HomologationSelectedCourse,
} from '../../types';
import StudentInfoCard from './components/StudentInfoCard';
import CurriculumGrid from './components/CurriculumGrid';
import HomologationCoursesTable from './components/HomologationTable';
import HomologationModal from './components/HomologationModal';
import IncompleteCoursesModal from './components/IncompleteCoursesModal';

export default function HomologationDetail() {
  const { t } = useTranslation(TranslationsKeys.HOMOLOGATION_DETAIL);
  const { requestId } = useParams<{ requestId: string }>();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState<HomologationRequestDetail | null>(null);
  const [sendingSignature, setSendingSignature] = useState(false);
  const [downloadingActa, setDownloadingActa] = useState(false);

  // Modal state
  const [
    modalCourse,
    setModalCourse,
  ] = useState<HomologationSelectedCourse | null>(null);
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(false);
    const result = await getHomologationRequestDetail(Number(requestId));
    if (result.error) {
      setError(true);
    } else {
      setData(result.data ?? null);
    }
    setLoading(false);
  }, [requestId]);

  const refreshData = useCallback(async () => {
    const result = await getHomologationRequestDetail(Number(requestId));
    if (!result.error) {
      setData(result.data ?? null);
    }
  }, [requestId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleHomologate = useCallback((course: HomologationSelectedCourse) => {
    setModalCourse(course);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalCourse(null);
  }, []);

  const handleDownloadActa = useCallback(async () => {
    if (!data) return;
    setDownloadingActa(true);
    const result = await downloadActaPdf(data.responseId);
    setDownloadingActa(false);
    if (result.error || !result.data) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: 'Error al descargar el acta de homologación.',
      });
      return;
    }
    const url = window.URL.createObjectURL(result.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `acta-homologacion-${data.responseId}.pdf`;
    link.click();
    window.URL.revokeObjectURL(url);
  }, [data]);

  const handleSendToSignature = useCallback(async () => {
    if (!data) return;

    // Check if all courses are processed
    const pendingCourses = data.selectedCourses.filter(
      (c) => c.status === 'pending',
    );

    if (pendingCourses.length > 0) {
      setShowIncompleteModal(true);
      return;
    }

    setSendingSignature(true);
    const result = await sendToSignature(data.responseId);
    setSendingSignature(false);

    if (result.error) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: 'Error al enviar la solicitud para firma.',
      });
      return;
    }

    addToast({
      icon: 'success',
      color: 'success',
      text: 'Solicitud enviada para firma correctamente.',
    });
    history.push('/homologation-requests');
  }, [data, history]);

  if (loading) {
    return (
      <div className="mx-3">
        <Loading insideCard />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-3">
        <DisplayError
          insideCard
          title={t('error.title')}
          textBody={t('error.body')}
          retryAction={fetchData}
        />
      </div>
    );
  }

  const isReadOnly = data.status === 'pending_signature';

  return (
    <div className="mx-3">
      <Card className="p-4">
        <section className="mb-4">
          <Row>
            <Col xs={12} sm={3}>
              <GoBackToListButton path="/homologation-requests" />
            </Col>
            <Col xs={12} sm={{ size: 6, offset: 3 }}>
              <StudentInfoCard
                studentName={data.studentName}
                studentRut={data.studentRut}
                programName={data.programName}
              />
            </Col>
          </Row>
        </section>

        <CurriculumGrid
          semesters={data.curriculum.semesters}
          onDownloadDocumentation={() => {
            addToast({
              icon: 'information',
              color: 'warning',
              text: 'Funcionalidad de descarga en desarrollo.',
            });
          }}
        />

        <HomologationCoursesTable
          courses={data.selectedCourses}
          requestId={data.responseId}
          onHomologate={handleHomologate}
          onFileChange={refreshData}
          readOnly={isReadOnly}
        />

        {/* Action buttons */}
        <div
          className="d-flex justify-content-end mt-4"
          style={{ gap: '1rem' }}
        >
          <div style={{ width: 320 }}>
            <Button
              text={t('downloadActa')}
              icon="download"
              outlined
              fullwidth
              onClick={handleDownloadActa}
              loading={downloadingActa}
            />
          </div>
          {!isReadOnly && (
            <div style={{ width: 280 }}>
              <Button
                text={t('sendToSignature')}
                fullwidth
                onClick={handleSendToSignature}
                loading={sendingSignature}
              />
            </div>
          )}
        </div>
      </Card>

      {/* Homologation Modal */}
      {!isReadOnly && (
        <HomologationModal
          isOpen={!!modalCourse}
          onClose={handleModalClose}
          course={modalCourse}
          requestId={data.responseId}
          onSuccess={refreshData}
        />
      )}

      {/* Incomplete Courses Modal */}
      {!isReadOnly && (
        <IncompleteCoursesModal
          isOpen={showIncompleteModal}
          onClose={() => setShowIncompleteModal(false)}
        />
      )}
    </div>
  );
}
